import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import type { HumanStepRequestedPayload, ThreadEntry } from "../../../../api/threads/liveMessages";
import { newClientKey } from "../../../../api/threads/threadsApi";
import { parseCollectionVersionRef } from "../../../../shared/references/contentReferences";
import { FORM_HELP_TEXT_CLASS } from "../../../../ui/styles/formStyles";
import { answeringStep, ChangeSetStepContext } from "../../../processAuthoring/draft/changeSetStep";
import { editedItemOf } from "../../entries/humanStepResponse";
import { parseEditedItem } from "../../prepare/author/editedItem";
import { answerWithChangeSet } from "../../prepare/author/editStep";
import { useThreadContext } from "../threadPageContext";
import EditedItemEditor from "./EditedItemEditor";

interface EditStepViewProps {
  readonly entry: ThreadEntry;
  readonly payload: HumanStepRequestedPayload;
}

/**
 * The "Edit an item" step waiting in its Thread (ADR-0031 D2): the editor for what it edits.
 * Saving answers this step with the change set it saved into; the review tail then decides
 * it in this Thread.
 */
export default function EditStepView({ entry, payload }: EditStepViewProps) {
  const { t } = useTranslation();
  const { threadId, overview } = useThreadContext();
  const text = editedItemOf(payload);
  const edited = text === null ? null : parseEditedItem(text);
  const step = useMemo(
    () =>
      answeringStep(async (changeSetId) => {
        await answerWithChangeSet(threadId, entry.number, changeSetId, newClientKey());
        return threadId;
      }, threadId),
    [threadId, entry.number],
  );
  const commit = parseCollectionVersionRef(overview.bindings.content_version)?.commit ?? null;

  if (edited === null || commit === null) return <p className={FORM_HELP_TEXT_CLASS}>{t("threads.editStep.noItem")}</p>;
  return (
    <ChangeSetStepContext.Provider value={step}>
      <EditedItemEditor edited={edited} projectId={overview.projectId} contentCommit={commit} />
    </ChangeSetStepContext.Provider>
  );
}
